import { useState } from 'react';
import { login, recover, signup } from '../identity.js';

export default function SignIn({ arrival = null }) {
  const [mode, setMode] = useState('signin');   // 'signin' | 'signup' | 'recover'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  // GoTrue has no resend endpoint, so an unconfirmed account is otherwise a
  // dead end: the link has expired and nothing on the page can issue another.
  // A recovery link is the way out — its token exchange returns a session, so
  // it signs you in whether or not the address was ever confirmed.
  const [stuck, setStuck] = useState(false);
  const [error, setError] = useState(arrival?.kind === 'error' ? arrival.message : null);
  const [notice, setNotice] = useState(
    arrival?.kind === 'signup' ? 'Email confirmed. Sign in below.' : null
  );

  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;

  async function submit(e) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      if (mode === 'recover') {
        await recover(email);
        setNotice('If that address has an account, a reset link is on its way.');
      } else if (mode === 'signup') {
        const { confirmationRequired } = await signup(email, password);
        if (confirmationRequired) {
          setNotice('Check your email to confirm the account, then sign in.');
          setMode('signin');
        }
      } else {
        await login(email, password);   // the session change swaps this screen out
      }
    } catch (err) {
      setError(err.message);
      setStuck(/needs confirming/i.test(err.message));
    } finally {
      setBusy(false);
    }
  }

  async function sendLink() {
    setBusy(true);
    setError(null);
    try {
      await recover(email);
      setStuck(false);
      setNotice('Sent. Open the link in that email and you will be signed straight in.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const label = mode === 'signup' ? 'Create account' : mode === 'recover' ? 'Send reset link' : 'Sign in';

  return (
    <div className="gate">
      <form className="gate-card" onSubmit={submit}>
        <div className="gate-wordmark">Longhand</div>
        <p className="gate-sub">
          {mode === 'signup' ? 'A quiet place to write, on every device you use.'
            : mode === 'recover' ? 'We will email you a link to set a new password.'
            : 'Sign in to your writing.'}
        </p>

        <label className="gate-field">
          <span>Email</span>
          <input
            type="email" name="email" autoComplete="email" required autoFocus
            value={email} onChange={e => setEmail(e.target.value)}
          />
        </label>

        {mode !== 'recover' && (
          <label className="gate-field">
            <span>Password</span>
            <input
              type="password" name="password" required minLength={8}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              value={password} onChange={e => setPassword(e.target.value)}
            />
          </label>
        )}

        {offline && <p className="gate-note is-bad">You appear to be offline. Signing in needs a connection.</p>}
        {error && <p className="gate-note is-bad">{error}</p>}
        {stuck && (
          <button type="button" className="gate-rescue" onClick={sendLink} disabled={busy}>
            Email me a sign-in link instead
          </button>
        )}
        {notice && <p className="gate-note is-ok">{notice}</p>}

        <button className="gate-submit" type="submit" disabled={busy}>
          {busy ? 'One moment…' : label}
        </button>

        <div className="gate-alt">
          {mode === 'signin' && (
            <>
              <button type="button" onClick={() => { setMode('signup'); setError(null); }}>Create an account</button>
              <span className="gate-dot">·</span>
              <button type="button" onClick={() => { setMode('recover'); setError(null); }}>Forgot password</button>
            </>
          )}
          {mode !== 'signin' && (
            <button type="button" onClick={() => { setMode('signin'); setError(null); }}>Back to sign in</button>
          )}
        </div>
      </form>
    </div>
  );
}
